import { chain, isObject, isString, map, split, get, omit, includes } from "lodash"
import { getBasketLabel } from "helpers/swaps"

const AXIOMA_ID_CURRENCY = /CSH_[A-Z]{3}__/

export function treatAsFactor(assetClass) {
  return assetClass === "currency"
}

export function treatAsBasket(assetClass) {
  return assetClass === "swap"
}

export function getTickerFromContributorId(contributorId) {
  return chain(contributorId)
    .split(".")
    .dropRight(1)
    .join(".")
    .value()
}

export function getTickerAndMicFromContributor(contributor) {
  let mic = ""
  let ticker = contributor.id

  switch (contributor.assetClass) {
    case "equity": {
      if (includes(contributor.id, ".")) {
        const parts = split(contributor.id, ".")
        mic = parts.pop()
        ticker = parts.join(".")
      }
      break
    }
    case "currency":
      if (isString(ticker) && ticker.match(AXIOMA_ID_CURRENCY)) {
        ticker = ticker.substr(4, 3)
      }
      mic = "CURR"
      break
    case "swap":
      mic = "_SWP"
      break
    case "commodity":
      mic = "_COM"
      break
    case "index":
      mic = "_IND"
      break
    case "fixedIncome":
      mic = "_FIX"
      break
  }
  return { ticker, mic }
}

export function getTickerAndMic(assetClass, id, ticker, mic) {
  switch (assetClass) {
    case "currency":
      if (id.match(AXIOMA_ID_CURRENCY)) {
        id = id.substr(4, 3)
      }
      return { ticker: id, mic: "CURR" }
    case "swap":
      return { ticker: id, mic: "_SWP" }
    case "commodity":
      return { ticker: id, mic: "_COM" }
    case "index":
      return { ticker: id, mic: "_IND" }
    case "fixedIncome":
      return { ticker: id, mic: "_FIX" }
    default:
      return { ticker, mic }
  }
}

export function getTickerMicAndId(assetClass, id, ticker, mic) {
  if (assetClass === "other") {
    return { id, ticker: id, mic: "" }
  }

  return { id, ...getTickerAndMic(assetClass, id, ticker, mic) }
}

export function getAssetClassFromMic(mic) {
  switch (mic) {
    case "CURR":
      return "currency"
    case "_SWP":
      return "swap"
    case "_COM":
      return "commodity"
    case "_IND":
      return "index"
    case "_FIX":
      return "fixedIncome"
    default:
      return "equity"
  }
}

export function formatLabel(position) {
  const { assetClass, id, indexId, ticker, country } = position
  const securityId = ticker || id || indexId || "Unknown"
  const formattedSecurityId = assetClass === "swap" ? getBasketLabel(securityId) : securityId
  return (!assetClass || !country || country === "US") ? formattedSecurityId : `${formattedSecurityId} ${country}`
}

export function computePositionSet(assetClass, ticker, mic, modelProviderId, evaluateAs, internalId) {
  return chain([{ assetClass, ticker, mic, modelProviderId, evaluateAs, internalId }])
    .groupBy("assetClass")
    .mapKeys(assetClassKeyMapIteratee)
    .mapValues(securities =>
      chain(securities)
        .map(position => ({
          id: getPositionId(position),
          economicExposure: position.evaluateAs === "short" ? -1 : 1,
        }),
        )
        .value(),
    )
    .value()
}

export function compareId(id1, id2) {
  // if model provider id is provided, check that
  return (id1.modelProviderId && id1.modelProviderId === id2.modelProviderId)
    || (id1.internalId && id1.internalId === id2.internalId)
    // else check ticker and mic
    || (id1.ticker === id2.ticker && id1.mic === id2.mic)
}

export function computeSecurityProfilePositionSet({ assetClass, ticker, mic, modelProviderId, startDate }) {
  if (assetClass === "swap") {
    return { type: "SWAP", id: ticker }
  }
  return {
    dates: [{
      date: startDate,
      ...computePositionSet(assetClass, ticker, mic, modelProviderId),
    }],
  }
}

export const positionsComparator = (pos1, pos2) => {
  if (isObject(pos1.id)) { return compareId(pos1.id, pos2.id) }
  return pos1.assetClass === pos2.assetClass && pos1.id === pos2.id
}

export function getPositionId(position) {
  let id = null

  switch (position.assetClass) {
    case "equity":
      id = {
        ticker: position.ticker,
        mic: position.mic,
        internalId: position.internalId,
      }

      if (position.modelProviderId) {
        id.modelProviderId = position.modelProviderId
      }
      break
    case "fixedIncome":
      id = {
        isin: position.ticker,
      }
      break
    default:
      id = position.ticker
      break
  }

  return id
}

export const isUnmodeled = security => {
  const { description, assetClass, isUnmodeled } = security
  return assetClass === "swap" ? isUnmodeled === true : !description
}

export const computeSecuritiesDescriptors = securities => map(securities, s => {
  const value = formatLabel(s)
  return {
    ...s,
    value,
    label: value,
  }
})

export function getAssetClassFromType(type) {
  switch (type) {
    case "equities":
      return "equity"
    case "otherAssets":
      return "other"
    case "swaps":
      return "swap"
    case "currencies":
      return "currency"
    case "fixedIncome":
      return "fixedIncome"
    case "indexes":
      return "index"
    case "commodities":
      return "commodity"
  }
  return type
}

// The security parameter is never used but is needed to match the iteratee signature of the lodash mapKeys function
export function assetClassKeyMapIteratee(security, key) {
  switch (key) {
    case "equity":
      return "equities"
    case "currency":
      return "currencies"
    case "swap":
      return "swaps"
    case "commodity":
      return "commodities"
    case "index":
      return "indexes"
    case "otherAsset":
    case "other":
      return "otherAssets"
    default:
      return key
  }
}

// We create contributors by merging all data maps (composition, performance, risk, ...) together
// If one of the maps has a notHeld value of false, notHeld will be false
export function getIsNoLongerHeldFromContributor(contributor) {
  return contributor.economicExposure == null && !get(contributor, "notHeld")
}

export function convertImpliedReturnsIdToUniversalId(position) {
  if (isObject(position.id)) {
    return omit(position.id, "modelProviderId")
  }
  return { ticker: position.id }
}
